<template>
  <div class="pb-1">
    <e-filters
      :searching="fetching"
      @search="filterData"
      @reset="resetFiltersLocal"
    >
      <b-row>
        <b-col md="6">
          <e-store-combo
            id="price-suggestion-configuration-filter-store"
            v-model="filters.storeId"
            :required="false"
          />
        </b-col>
        <b-col md="6">
          <e-search-sku
            id="price-suggestion-configuration-filter-sku"
            v-model="filters.skuId"
            :required="false"
            :only-active="true"
            :include-fixed-assets="true"
          />
        </b-col>
      </b-row>
    </e-filters>

    <b-card-actions
      action-refresh
      :show-loading="fetching"
      @refresh="filterData"
    >
      <b-table
        ref="price-suggestion-configuration-table"
        show-empty
        responsive
        striped
        class="bordered"
        :empty-text="getEmptyTableMessage($tc('PRICE_SUGGESTION_CONFIGURATION.NAME'), 'female', queryExecuted)"
        :fields="fields"
        :items="sortedConfigurations"
      >
        <template #cell(storeId)="row">
          <b-badge
            v-if="!row.item.storeId"
            variant="light-secondary"
          >
            {{ $t('Geral') }}
          </b-badge>
          <span v-else>{{ getStoreName(row.item.storeId) }}</span>
        </template>

        <template #cell(skuId)="row">
          <b-badge
            v-if="!row.item.skuId"
            variant="light-secondary"
          >
            {{ $t('Geral') }}
          </b-badge>
          <span v-else>{{ row.item.skuId }}{{ row.item.skuName ? ' - ' + row.item.skuName : '' }}</span>
        </template>

        <template #cell(action)="row">
          <e-grid-actions
            :show-update="$can('Update', 'SkuPriceSuggestionConfiguration')"
            :show-delete="$can('Delete', 'SkuPriceSuggestionConfiguration')"
            @update="showUpdateSidebar(row)"
            @delete="deleteConfigurationLocal(row)"
          />
        </template>
      </b-table>
    </b-card-actions>

    <price-suggestion-configuration-sidebar
      ref="price_suggestion_configuration_sidebar"
      @save="getData"
    />

    <fab
      v-if="$can('Create', 'SkuPriceSuggestionConfiguration')"
      :main-tooltip="$t('Adicionar Configuração de Sugestão de Preço')"
      @click="showCreateSidebar"
    />
  </div>
</template>

<script>
import { BRow, BCol, BTable, BBadge } from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import { EFilters, EGridActions } from '@/views/components'
import EStoreCombo from '@/views/components/inputs/EStoreCombo.vue'
import ESearchSku from '@/views/components/inputs/ESearchSku.vue'
import fab from '@/views/components/FAB.vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import PriceSuggestionConfigurationSidebar from './PriceSuggestionConfigurationSidebar.vue'

export default {
  components: {
    BRow,
    BCol,
    BTable,
    BBadge,
    BCardActions,
    EFilters,
    EGridActions,
    EStoreCombo,
    ESearchSku,
    fab,
    PriceSuggestionConfigurationSidebar,
  },

  data() {
    return {
      queryExecuted: false,
      fetching: false,
    }
  },

  computed: {
    ...mapState('pages/catalog/priceSuggestionConfigurations', {
      configurations: 'configurations',
      filters: 'filters',
    }),
    ...mapGetters('app', ['allStoreOptions']),

    // A API já devolve ordenado por SkuId e depois StoreId (Geral antes de específico);
    // reforçamos a ordem exibida por Produto e depois Loja no client (nulos/Geral primeiro).
    sortedConfigurations() {
      return [...(this.configurations || [])].sort((a, b) => {
        if ((a.skuId || 0) !== (b.skuId || 0)) return (a.skuId || 0) - (b.skuId || 0)
        return (a.storeId || 0) - (b.storeId || 0)
      })
    },

    fields() {
      return [
        {
          label: this.$t('Ações'),
          key: 'action',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Id'),
          key: 'id',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Loja'),
          key: 'storeId',
          thClass: 'text-center',
          tdClass: 'text-left',
          thStyle: { width: '300px' },
        },
        {
          label: this.$t('Produto'),
          key: 'skuId',
          class: 'text-center',
          thStyle: { width: '100px' },
        },
        {
          label: this.$t('Margem Mínima (%)'),
          key: 'minimumMarginPercent',
          class: 'text-center',
          thStyle: { width: '180px' },
        },
        {
          label: this.$t('Variação Máxima (%)'),
          key: 'maximumVariationPercent',
          class: 'text-center',
          thStyle: { width: '180px' },
        },
      ]
    },
  },

  methods: {
    ...mapActions('pages/catalog/priceSuggestionConfigurations', [
      'fetchConfigurations',
      'deleteConfiguration',
      'resetFilters',
    ]),

    getStoreName(storeId) {
      const store = this.allStoreOptions?.find(s => s.value === storeId)
      return store ? store.label : storeId
    },

    async getData() {
      this.$nextTick(async () => {
        try {
          this.fetching = true
          await this.fetchConfigurations()
          this.queryExecuted = true
        } catch (error) {
          this.showGenericError({ error })
        } finally {
          this.fetching = false
        }
      })
    },

    filterData() {
      this.getData()
    },

    resetFiltersLocal() {
      this.resetFilters()
      this.filterData()
    },

    showCreateSidebar() {
      this.$refs.price_suggestion_configuration_sidebar.show()
    },

    showUpdateSidebar(row) {
      this.$refs.price_suggestion_configuration_sidebar.show({ item: row.item })
    },

    async deleteConfigurationLocal(row) {
      const { id } = row.item
      try {
        const confirm = await this.confirm()
        if (confirm) {
          await this.deleteConfiguration(id)
          this.getData()
          this.showSuccess({
            message: this.$t('Configuração removida com sucesso'),
          })
        }
      } catch (error) {
        this.showError({ error })
      }
    },
  },
}
</script>

<style></style>
